import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { ChevronRightIcon, Pencil2Icon } from "@radix-ui/react-icons";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useGetInstance } from "src/api/General/instance";
import {
  RoleData,
  useCreateRole,
  useGetRoles,
  useUpdateRole,
} from "src/api/Misc/Settings/roles/roles";
import InputHeading from "src/components/ui/Headings/InputHeading";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { TextInput } from "src/components/ui/text-input";

export default function Roles() {
  const { data: roles } = useGetRoles();
  const { instance } = useGetInstance();
  const navigate = useNavigate();
  const { tab } = useParams();
  console.log(tab);
  if (!tab) {
    navigate(`/settings/roles/internal`, {
      replace: true,
    });
  } else {
    if (tab !== "internal" && tab !== "external") {
      navigate(`/settings/roles/internal`, {
        replace: true,
      });
    }
  }

  const createRoleMutation = useCreateRole();
  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  const [createInternalRoleName, setCreateInternalRoleName] = useState("");
  const [createExternalRoleName, setCreateExternalRoleName] = useState("");

  useEffect(() => {
    if (createRoleMutation.isSuccess) {
      setCreateRoleOpen(false);
      setCreateInternalRoleName("");
      setCreateExternalRoleName("");
    }
  }, [createRoleMutation.isSuccess]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 items-center border-b border-gray-200/70 px-8 text-sm">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/settings`}>
            <div className="text-gray-800">{instance?.name}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{"Roles"}</div>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <div className="mx-auto max-w-2xl pt-20">
          <div className="space-y-1 border-gray-200/70 pb-2">
            <div className="text-xl font-medium">Roles</div>
            <div className="text-sm font-light text-gray-600">
              Define roles for your internal and external teams
            </div>
          </div>
          <div className="mt-8">
            <Tabs
              defaultValue="home"
              value={tab}
              className="flex flex-1 flex-col overflow-auto pt-1"
              onValueChange={(value) => {
                navigate(`/settings/roles/${value}`);
              }}
            >
              <TabsList className="shrink-0 border-gray-200/70">
                <TabsTrigger
                  className="font-normal text-gray-500"
                  value="internal"
                >
                  Internal
                </TabsTrigger>
                <TabsTrigger
                  className="font-normal text-gray-500"
                  value="external"
                >
                  External
                </TabsTrigger>
              </TabsList>
              <div className="flex-1 overflow-auto">
                <TabsContent value="internal" className="h-full">
                  <div className="mt-6 space-y-6">
                    {/* <div className="text-sm font-light leading-relaxed text-gray-600">
                      Internal roles are defined under internal teams. E.g.{" "}
                      <span className="font-medium text-gray-600">
                        Customer Success Manager
                      </span>{" "}
                      would be a role under the{" "}
                      <span className="font-medium text-gray-600">
                        Customer Success
                      </span>{" "}
                      team.
                    </div> */}
                    <div className="flex justify-end">
                      <Dialog
                        open={createRoleOpen}
                        onOpenChange={setCreateRoleOpen}
                      >
                        <DialogTrigger asChild>
                          <Button variant={"secondary"}>
                            Create Internal Role
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>New Role</DialogTitle>
                          </DialogHeader>
                          <div className="flex flex-col space-y-4 py-4">
                            <div className="space-y-1">
                              <InputHeading heading="Name" />
                              <TextInput
                                className="w-72"
                                value={createInternalRoleName}
                                onChange={(e) =>
                                  setCreateInternalRoleName(e.target.value)
                                }
                                placeholder="e.g. Implementation Manager"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant={"secondary"}
                              onClick={() => setCreateRoleOpen(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => {
                                createRoleMutation.mutate({
                                  name: createInternalRoleName,
                                  type: "internal",
                                });
                              }}
                              disabled={
                                createRoleMutation.isLoading ||
                                !createInternalRoleName.trim()
                              }
                            >
                              {createRoleMutation.isLoading
                                ? "Creating..."
                                : "Create"}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <div className="rounded-md border border-gray-200 p-6 pb-3 shadow-sm">
                      <div className="flex items-center border-b border-gray-200 pb-3 font-normal text-gray-500">
                        <div className="flex-1 text-sm leading-none">Role</div>
                        <div className="w-16" />

                        {/* <div className="w-40 text-sm leading-none">Type</div>
                      <div className="w-40 text-sm leading-none">
                        Created by
                      </div> */}
                      </div>
                      {roles
                        ?.filter((role) => role.type === "internal")
                        .map((role) => (
                          <RoleRow key={role._id} role={role} />
                        ))}
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="external" className="h-full overflow-auto">
                  <div className="mt-6 space-y-6">
                    {/* <div className="text-sm font-light leading-relaxed text-gray-600">
                      Internal roles are defined under internal teams. E.g.{" "}
                      <span className="font-medium text-gray-600">
                        Customer Success Manager
                      </span>{" "}
                      would be a role under the{" "}
                      <span className="font-medium text-gray-600">
                        Customer Success
                      </span>{" "}
                      team.
                    </div> */}
                    <div className="flex justify-end">
                      <Dialog
                        open={createRoleOpen}
                        onOpenChange={setCreateRoleOpen}
                      >
                        <DialogTrigger asChild>
                          <Button variant={"secondary"}>
                            Create External Role
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>New Role</DialogTitle>
                          </DialogHeader>
                          <div className="flex flex-col space-y-4 py-4">
                            <div className="space-y-1">
                              <InputHeading heading="Name" />
                              <TextInput
                                className="w-72"
                                value={createExternalRoleName}
                                onChange={(e) =>
                                  setCreateExternalRoleName(e.target.value)
                                }
                                placeholder="e.g. Champion"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant={"secondary"}
                              onClick={() => setCreateRoleOpen(false)}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => {
                                createRoleMutation.mutate({
                                  name: createExternalRoleName,
                                  type: "external",
                                });
                              }}
                              disabled={
                                createRoleMutation.isLoading ||
                                !createExternalRoleName.trim()
                              }
                            >
                              {createRoleMutation.isLoading
                                ? "Creating..."
                                : "Create"}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <div className="rounded-md border border-gray-200 p-6 pb-3 shadow-sm">
                      <div className="flex items-center border-b border-gray-200 pb-3 font-normal text-gray-500">
                        <div className="flex-1 text-sm leading-none">Role</div>
                        {/* <div className="w-40 text-sm leading-none">Type</div>
                      <div className="w-40 text-sm leading-none">
                        Created by
                      </div> */}
                      </div>
                      {roles
                        ?.filter((role) => role.type === "external")
                        .map((role) => (
                          <RoleRow key={role._id} role={role} />
                        ))}
                    </div>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}

function RoleRow({ role }: { role: RoleData }) {
  const [isEditing, setIsEditing] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [name, setName] = useState(role.name);
  const updateRoleMutation = useUpdateRole();
  return (
    <div className="flex h-10 items-center border-b border-gray-100 last:border-0">
      <div className="flex-1 truncate text-sm font-medium">{role.name}</div>
      <div className="flex w-16 justify-end">
        <DropdownMenu
          open={optionsOpen}
          onOpenChange={(isOpen) => {
            setOptionsOpen(isOpen);
          }}
        >
          <DropdownMenuTrigger asChild>
            <Button
              variant={"ghost"}
              className={clsx(
                "h-6 w-6 rounded p-0 hover:bg-gray-200/60 group-hover:visible",
                !optionsOpen ? "bg-transparent" : "bg-gray-200/60"
              )}
            >
              <EllipsisHorizontalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-44" align="end">
            <DropdownMenuGroup>
              <DropdownMenuItem
                // className="py-1 focus:bg-gray-50 focus:text-red-600"
                onClick={(e) => {
                  setIsEditing(true);
                  e.stopPropagation();
                }}
                // className="py-1"
              >
                <Pencil2Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="ml-2 text-sm">Edit</span>
              </DropdownMenuItem>
              {/* <DropdownMenuItem
                      // className="py-1 focus:bg-gray-50 focus:text-red-600"
                      className="focus:bg-red-50 focus:text-red-600"
                      onClick={(e) => {
                        setIsDeleting(true);
                        e.stopPropagation();
                      }}
                    >
                      <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="ml-2 text-sm">Delete</span>
                    </DropdownMenuItem> */}
              {/* <DropdownMenuItem>Delete</DropdownMenuItem> */}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogContent
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
          >
            <DialogHeader>
              <DialogTitle>Edit Role</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="space-y-1">
                <InputHeading heading="Name" />
                <TextInput
                  className="w-72"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                  placeholder="Resource name..."
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant={"secondary"} onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button
                disabled={
                  name.trim() === role.name.trim() || name.trim() === ""
                }
                onClick={() => {
                  updateRoleMutation.mutate({
                    _id: role._id,
                    name: name,
                  });
                  setIsEditing(false);
                }}
              >
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
